import React from 'react';
import { cx, eyebrowClass } from './Primitives';
import { ageSwatch } from './Controls';

/* ============================================================================
   The financial components (DESIGN-SYSTEM §4, §9): money, its side, its age,
   promises, freshness and how much attention an account needs.

   Tenant-neutral, like everything in this package: the amounts arrive
   already formatted by the company's own formatter (its currency, its digit
   grouping), and these only decide how they are set — the weight, the
   figures, the colour that says what the money means.

   The colour rule for money: an amount is ink. It is coloured only when the
   colour is the point — red for money overdue past the company's line, jade
   for money collected or settled, the credit tone for money held for a
   customer. Never green for "a big number".
   ============================================================================ */

export type AmountTone = 'plain' | 'overdue' | 'collected' | 'credit' | 'muted';
const AMOUNT_TONE: Record<AmountTone, string> = {
    plain: 'text-label', overdue: 'text-dang', collected: 'text-pos', credit: 'text-info', muted: 'text-label-3',
};
const AMOUNT_SIZE = {
    body: 'text-body font-semibold',
    lead: 'text-subhead font-semibold',
    figure: 'figure text-figure leading-none',
    'figure-sm': 'figure text-figure-sm leading-none',
} as const;

/** An amount, set in tabular figures so a column of them lines up digit under digit. */
export function Amount({ value, tone = 'plain', size = 'body', className, label }: {
    /** Already formatted: "₹4,20,000", "₹4.2L". */
    value: React.ReactNode;
    tone?: AmountTone;
    size?: keyof typeof AMOUNT_SIZE;
    className?: string;
    /** When the visible figure is shortened ("₹4.2L"), the full amount for the ear. */
    label?: string;
}) {
    return (
        <span aria-label={label} className={cx('num whitespace-nowrap', AMOUNT_SIZE[size], AMOUNT_TONE[tone], className)}>{value}</span>
    );
}

/**
 * Which side of the book a balance is on: Dr, owed to the company; Cr, held
 * for the customer. Said in words for the ear ("owed to you", "in credit").
 */
export function DrCr({ side, className }: { side: 'Dr' | 'Cr'; className?: string }) {
    return (
        <abbr title={side === 'Dr' ? 'Debit: owed to you' : 'Credit: held for the customer'}
            className={cx('no-underline text-caption font-semibold tracking-caps', side === 'Dr' ? 'text-label-3' : 'text-info', className)}>
            {side}
        </abbr>
    );
}

/**
 * One age band, as a square of its colour and its words ("91–135 days"):
 * band `i` of `n`, youngest first, coloured from the same ramp as the bars.
 */
export function AgeTag({ index, of, children, className }: { index: number; of: number; children: React.ReactNode; className?: string }) {
    return (
        <span className={cx('inline-flex items-center gap-1.5 text-secondary text-label-2 whitespace-nowrap', className)}>
            <span aria-hidden="true" className={cx('size-2 flex-none', ageSwatch(index, of))} />
            {children}
        </span>
    );
}

export type PromiseState = 'due' | 'upcoming' | 'missed' | 'kept' | 'not-kept';
const PROMISE: Record<PromiseState, { cls: string; word: string }> = {
    due: { cls: 'bg-warn-bg text-warn', word: 'Promise due today' },
    upcoming: { cls: 'bg-accent-tint text-accent-text', word: 'Promised' },
    // Its date has gone and nobody has answered it yet: waiting (amber), not broken (crimson is "not kept").
    missed: { cls: 'bg-warn-bg text-warn', word: 'Promise date passed' },
    kept: { cls: 'bg-pos-bg text-pos', word: 'Promise kept' },
    'not-kept': { cls: 'bg-dang-bg text-dang', word: 'Promise not kept' },
};

/**
 * A promise to pay, as a tag: its state in words, and what was promised when
 * it helps ("₹91K by 11 Oct"). A promise is expected money, never received
 * money: it is never jade until somebody marks it kept.
 */
export function PromiseTag({ state, detail, className }: { state: PromiseState; detail?: React.ReactNode; className?: string }) {
    const p = PROMISE[state];
    return (
        <span className={cx('inline-flex items-center gap-1.5 rounded-sm px-2 h-5 text-caption font-semibold leading-none whitespace-nowrap', p.cls, className)}>
            {p.word}{detail && <span className="font-medium num">· {detail}</span>}
        </span>
    );
}

export type FreshnessState = 'current' | 'syncing' | 'stale' | 'stopped' | 'none';
const FRESH: Record<FreshnessState, { dot: string; ink: string }> = {
    current: { dot: 'bg-mark animate-pulse-mark', ink: 'text-label-3' },
    syncing: { dot: 'bg-accent animate-pulse', ink: 'text-label-3' },
    stale: { dot: 'bg-signal-warn', ink: 'text-warn' },
    stopped: { dot: 'bg-dang', ink: 'text-dang' },
    none: { dot: 'bg-label-4', ink: 'text-label-3' },
};

/**
 * How current the accounting figures are, on one line: a square in the state's
 * colour and the words ("Updated from TallyPrime 2 minutes ago"). The square is
 * the brand's settle mark, and breathes slowly only while the source is current.
 */
export function Freshness({ state, children, className }: { state: FreshnessState; children: React.ReactNode; className?: string }) {
    const f = FRESH[state];
    return (
        <span className={cx('inline-flex items-center gap-2 text-secondary', f.ink, className)}>
            <span aria-hidden="true" className={cx('size-2 flex-none', f.dot)} />
            <span>{children}</span>
        </span>
    );
}

export type AttentionLevel = 'high' | 'medium' | 'low';
const ATTENTION: Record<AttentionLevel, { cls: string; word: string; edge: string }> = {
    high: { cls: 'bg-dang-bg text-dang', word: 'High', edge: 'before:bg-dang' },
    medium: { cls: 'bg-warn-bg text-warn', word: 'Medium', edge: 'before:bg-signal-warn' },
    low: { cls: 'bg-card-3 text-label-2', word: 'Low', edge: 'before:bg-label-4' },
};

/** How much a reason weighs, in a word and a tone. The reasons themselves are facts; this only orders them. */
export function AttentionTag({ level, className }: { level: AttentionLevel; className?: string }) {
    const a = ATTENTION[level];
    return <span className={cx('inline-flex items-center rounded-sm px-1.5 h-5 text-caption font-semibold leading-none', a.cls, className)}>{a.word}</span>;
}

/** The edge mark of a row that needs attention: a short vertical rule in the level's colour, on the row's left. */
export const attentionEdge = (level: AttentionLevel | null) =>
    level ? cx('relative before:absolute before:left-0 before:top-2 before:bottom-2 before:w-0.5 before:rounded-full', ATTENTION[level].edge) : '';

/**
 * A band of figures across one surface: a label over each value, divided by
 * hairlines — an account's balance, its overdue part, its next date. One
 * surface, never a grid of separate tiles.
 *
 * Below md the figures sit two abreast; `narrow="rows"` gives each its own row
 * instead, its label beside it — for figures too long to share a phone's width
 * (a statement's, to the paisa).
 */
export function FigureBand({ items, className, narrow = 'pairs' }: {
    items: readonly { label: React.ReactNode; value: React.ReactNode; note?: React.ReactNode }[];
    className?: string;
    narrow?: 'pairs' | 'rows';
}) {
    const rows = narrow === 'rows';
    return (
        <dl className={cx('grid md:flex md:divide-x divide-separator', !rows && 'grid-cols-2', className)}>
            {items.map((item, i) => (
                <div key={i} className={cx(
                    'min-w-0 md:flex-1 px-5 md:py-4 max-md:border-t max-md:border-separator',
                    rows ? 'py-3 max-md:grid max-md:grid-cols-[minmax(0,1fr)_auto] max-md:items-baseline max-md:gap-x-4' : 'py-4 max-md:odd:border-r',
                )}>
                    <dt className={eyebrowClass}>{item.label}</dt>
                    <dd className={cx('min-w-0', rows ? 'md:mt-1.5 max-md:text-right' : 'mt-1.5')}>{item.value}</dd>
                    {item.note && <dd className={cx('text-caption text-label-3 mt-1', rows && 'max-md:col-span-2 max-md:mt-0.5')}>{item.note}</dd>}
                </div>
            ))}
        </dl>
    );
}

/**
 * A share of a whole, with its numbers: "11 of 25 accounts · 44%". The bar is
 * a reading aid beside the words, toned by what the share means — never red
 * merely for being small, unless small is what is wrong.
 */
export function Meter({ share, label, tone = 'accent', className }: {
    share: number;
    label: string;
    tone?: 'accent' | 'pos' | 'warn' | 'dang';
    className?: string;
}) {
    const pct = Math.round(Math.min(1, Math.max(0, share)) * 1000) / 10;
    const fill = { accent: 'bg-accent', pos: 'bg-mark', warn: 'bg-signal-warn', dang: 'bg-signal-dang' }[tone];
    return (
        <div role="meter" aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} aria-label={label}
            className={cx('h-1.5 rounded-full bg-card-3 overflow-hidden', className)}>
            <div className={cx('h-full rounded-full transition-[width] duration-500', fill)} style={{ width: `${pct}%` }} />
        </div>
    );
}
